import { css, StyleSheet } from 'aphrodite';
import { MetaAuthFailureReason } from '../../../extViewmodels/sdk/models/Ads';
import {
	FACEBOOK_LOGIN_URL,
	META_AUTH_FAILURE_MESSAGES,
	requiresFacebookLogin,
} from '../../../models/utils/metaAuthFailure';
import { Button } from '../../components-v2/Button';
import { SPACE, THEME } from '../../components-v2/Theme/theme';

/** Props for {@link MetaAuthFailureNotice}. */
interface IMetaAuthFailureNoticeProps {
	/** Why Meta rejected the token; selects the guidance and which action comes first. */
	reason: MetaAuthFailureReason;
	/** `warning` while the connection still works, `danger` once it has stopped. Defaults to `danger`. */
	tone?: 'warning' | 'danger';
	/** Optional bold lead-in shown above the guidance. */
	title?: string;
	/** Starts reconnecting. Omit it where the surrounding UI already offers Reconnect. */
	onReconnect?: () => void;
	/** Shows the Reconnect action as busy. */
	isReconnecting?: boolean;
	/** Disables Reconnect while something else is changing the connection. */
	reconnectDisabled?: boolean;
}

/**
 * Tells the customer why Meta rejected their Facebook token and what to do about it. For a checkpoint or an
 * unconfirmed account the first step is logging in at facebook.com, so that link leads and Reconnect follows.
 * @param props See {@link IMetaAuthFailureNoticeProps}.
 */
export const MetaAuthFailureNotice = ({
	reason,
	tone = 'danger',
	title,
	onReconnect,
	isReconnecting,
	reconnectDisabled,
}: IMetaAuthFailureNoticeProps) => {
	const loginFirst = requiresFacebookLogin(reason);
	const reconnectButton = onReconnect ? (
		<Button
			variant={loginFirst ? 'outline' : 'primary'}
			size='sm'
			onClick={onReconnect}
			disabled={isReconnecting || reconnectDisabled}
			isLoading={isReconnecting}
		>
			Reconnect
		</Button>
	) : null;

	return (
		<div
			role='alert'
			className={css(styles.notice, tone === 'warning' ? styles.warning : styles.danger)}
			data-testid='meta-auth-failure-notice'
		>
			{title ? <strong className={css(styles.title)}>{title}</strong> : null}
			<p className={css(styles.message)}>{META_AUTH_FAILURE_MESSAGES[reason]}</p>
			{loginFirst || reconnectButton ? (
				<div className={css(styles.actions)}>
					{loginFirst ? (
						<Button asChild variant='primary' size='sm'>
							<a href={FACEBOOK_LOGIN_URL} target='_blank' rel='noopener noreferrer'>
								Log in at facebook.com
							</a>
						</Button>
					) : null}
					{reconnectButton}
				</div>
			) : null}
		</div>
	);
};

const styles = StyleSheet.create({
	notice: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[8],
		padding: SPACE[16],
		borderRadius: THEME.common.borderRadius,
		border: '1px solid',
		boxSizing: 'border-box',
		color: THEME.colors.text.dark,
		fontSize: THEME.typography.fontSizes.sm,
	},
	warning: {
		backgroundColor: THEME.colors.warning.light,
		borderColor: THEME.colors.warning.main,
	},
	danger: {
		backgroundColor: THEME.colors.danger.light,
		borderColor: THEME.colors.danger.main,
	},
	title: {
		fontWeight: THEME.typography.fontWeights.bold,
	},
	message: {
		margin: 0,
	},
	actions: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: SPACE[8],
	},
});
